import { useEffect, useMemo } from "react";
import dayjs from "dayjs";

import { buildStrategyPills } from "../strategy/strategyPills";
import { useRequirementsLoadsQuery } from "./useLoadFinderLoadsQueries";

const DEFAULT_RADIUS = 150;
const DEFAULT_OUTBOUND_MIN = 0;
const DEFAULT_OUTBOUND_MAX = 3000;

export const useActiveLoadsView = ({
  selectedTruck,
  truckPrefill,
  globalPrefill,
  mergedTruckPrefill,
  prefillReady,
  truckFiltersLoading,
  hasFilters,
  selectedFilter,
  filtersEnabled,
  applyRequirementParams,
}) => {
  // "Strategy exists" == the pill row would render at least one chip. Matches the
  // user-facing notion of a strategy being active.
  const hasStrategy = buildStrategyPills(truckPrefill, globalPrefill).length > 0;

  // The single get-loads view for strategy / truck-only modes, null when it shouldn't run.
  // The filter view is excluded via (hasStrategy || !hasFilters) so no stray get-loads fires
  // before the URL redirects to the default filter.
  const activeView = useMemo(() => {
    if (!prefillReady || !selectedTruck || truckFiltersLoading) return null;
    if (selectedFilter != null) return null;
    if (!hasStrategy && hasFilters) return null;
    if (!selectedTruck.location?.zip) return null;

    const activePrefill = filtersEnabled ? mergedTruckPrefill : null;

    const pickupDate = dayjs(selectedTruck.deliveryDate || undefined).format("YYYY-MM-DD");
    const pickupRadius = activePrefill?.pickupRadius ?? DEFAULT_RADIUS;
    const deliveryRadius = activePrefill?.deliveryRadius ?? DEFAULT_RADIUS;
    const outboundMin = activePrefill?.outboundDistanceMin ?? DEFAULT_OUTBOUND_MIN;
    const outboundMax = activePrefill?.outboundDistanceMax ?? DEFAULT_OUTBOUND_MAX;
    const minOffer = activePrefill?.minOffer ?? null;
    const minRpm = activePrefill?.minRpm ?? null;
    const trailerType = activePrefill?.trailerType ?? selectedTruck.trailerType;
    const pickupZip = activePrefill?.departureZip ?? selectedTruck.location.zip;
    const destinationZip = activePrefill?.destinationZip ?? null;

    return {
      requirementParams: {
        earnMoreOn: 2,
        minOffer,
        minRpm,
        outboundMin,
        outboundMax,
        searchParams: {
          trailerType,
          pickupZip,
          pickupState: selectedTruck.location.state,
          deliveryZip: destinationZip,
          deliveryState: null,
          pickupDateStart: pickupDate,
          pickupDateEnd: pickupDate,
          pickupRadius: String(pickupRadius),
          deliveryRadius: String(deliveryRadius),
        },
      },
      queryParams: {
        trailerType,
        pickup: {
          zip: pickupZip,
          placeName: selectedTruck.location.city,
          stateCode: selectedTruck.location.state,
        },
        delivery: destinationZip ? { zip: destinationZip } : null,
        pickupDateStart: pickupDate,
        pickupDateEnd: pickupDate,
        pickupRadius,
        deliveryRadius: destinationZip ? deliveryRadius : null,
      },
    };
  }, [prefillReady, selectedTruck, truckFiltersLoading, selectedFilter, hasStrategy, hasFilters, filtersEnabled, mergedTruckPrefill]);

  useEffect(() => {
    if (activeView) applyRequirementParams(activeView.requirementParams);
  }, [activeView, applyRequirementParams]);

  const requirementsLoadsQuery = useRequirementsLoadsQuery(activeView?.queryParams ?? null);

  return { hasStrategy, requirementsLoadsQuery };
};
